/**
 * Deeply clone an object.
 * @param {*} target
 * @param {*} source
 * @returns
 */
export const mergeDeep = (target, source) => {
  if (typeof target !== 'object') {
    target = {};
  }

  if (Array.isArray(target) && !Array.isArray(source)) {
    target = {};
  }

  Object.keys(source).forEach(key => {
    if (source[key] === null) {
      target[key] = null;
    } else if (Array.isArray(source[key])) {
      target[key] = source[key].slice();
    } else if (typeof source[key] === 'object') {
      target[key] = mergeDeep(target[key], source[key]);
    } else {
      target[key] = source[key];
    }
  });
  return target;
};

/**
 * Cases, we don't need no stinking cases.
 * @param {*} valueA
 * @param {*} valueB
 * @returns
 */
export function caseless(valueA, valueB) {
  if (valueA === valueB) {
    return 0;
  }

  // These two conditionals will put empty string, null, or undefined as the last items in a sort.
  if (valueA === null || valueA === undefined || valueA === '') {
    return 1;
  }

  if (valueB === null || valueB === undefined || valueB === '') {
    return -1;
  }

  return (valueA).toLowerCase().localeCompare((valueB).toLowerCase());
}

/**
 * Used to correctly handle sorting floats
 * @param valueA
 * @param valueB
 * @returns
 */
export function floatCompare(valueA, valueB) {
  const nA = parseFloat(valueA);
  const nB = parseFloat(valueB);
  if (valueA === valueB) {
    return 0;
  }

  // These two conditionals will put empty string, null, or undefined as the first items in a sort.
  if (Number.isNaN(nA)) {
    return -1;
  }

  if (Number.isNaN(nB)) {
    return 1;
  }
  return (nA > nB) ? 1 : -1;
}

/**
 * Sorts array of objects by a given prop and returns a copy
 * @param items
 * @param prop
 * @returns array
 */
export function sortOn(items, prop = 'label', isNumber = false) {
  if (!items || items.length < 2) {
    return items && items.length ? [...items] : [];
  }

  if (!Array.isArray(items)) {
    return [items];
  }

  if (isNumber) {
    return [...items].sort((a, b) => floatCompare(a[prop], b[prop]));
  }

  return [...items].sort((a, b) => caseless(a[prop], b[prop]));
}

export const createZoomOption = (label, value, extent) => ({ label, value, extent });
export const zoomableOption = (item, labelProp = 'label', valueProp = 'id') => {
  return createZoomOption(item[labelProp], item[valueProp], item.fields?.extent);
};

export function zoomablesOptions(zoomables, labelProp = 'label', valueProp = 'id') {
  return sortOn(zoomables, 'label').map((item) => zoomableOption(item, labelProp, valueProp));
}

export const getSectionChoices = (layout, sectionName, modelName) => {
  if (!layout || !layout.sections) {
    return [];
  }

  const section = layout.sections.find(s => s.name === sectionName);
  if (!section?.layout) {
    return [];
  }

  const type = section.layout.find(l => l.model.name === modelName);
  const choices = type?.possibleChoices ? type?.possibleChoices.map(item => ({
    label: item.name,
    id: item.id,
  })) : [];

  return choices;
}

export const processLayout = (layout) => {
  if (!layout || !layout.sections) {
    return layout;
  }

  const sections = layout.sections.map((section) => {
    const fields = section.layout.map(getStructure);
    // return { name: section.name, fields:fields.filter(f => f.type === FIELD_TYPES.TEXT || f.type === FIELD_TYPES.DATE || f.type === FIELD_TYPES.LONG_TEXT || f.type === FIELD_TYPES.LINK ) };
    return { name: section.name, fields: fields };
  });

  return sections;
}

function getStructure(field) {
  const model = field.model || {};
  const name = model.name || `unknown${model.id}`;
  const { label, linkFormat } = field;
  const required = !!field.required;
  const readOnly = !!field.readOnly;
  const disabled = false;
  const hidden = !!field.hidden;

  const dynField = {
    hidden,
    required,
    disabled,
    type: field.type,
    path: field.path,
    isArrayData: !!model.multiple,
    isStringId: !!model.isStringId,
    width: field.width || 100,
    render: {
      type: field.type,
      label,
      name,
      hidden,
      required,
      disabled,
      readOnly,
      linkFormat,
    }
  };

  if (field.possibleChoices) {
    const choices = field?.possibleChoices ? field?.possibleChoices.map(item => ({
      label: item.name,
      id: item.id,
    })) : [];

    dynField.render.choices = choices;
  }

  return dynField;
}

export const hasPermission = (permission, acl) => {
  return acl?.includes(permission) || false;
}

export const hasAllPermissions = (permissions, acl) => {
  for (let perm of permissions) {
    if (!acl?.includes(perm)) {
      return false;
    }
  }

  return true;
}

export const hasAnyPermissions = (permissions, acl) => {
  for (let perm of permissions)
    if (acl.includes(perm))
      return true;

  return false;
}

export function zeroPad(num, size = 3) {
  return num.toString().padStart(size, '0');
}

/**
 * Return a date string from either an ag grid cell object or string
 * @param inc : string | ag grid cell params
 * @returns string
 */
export function dateFormatter(inc) {
  if (inc instanceof Date) {
    return dateToString(inc);
  }

  if ((!inc || (typeof inc === 'object' && !inc.value))) {
    return '';
  }

  const date = new Date(typeof inc === 'object' ? inc.value : inc);
  return dateToString(date);
}

export function dateToString(date) {
  if (!date) {
    return '';
  }

  return `${zeroPad(date.getMonth() + 1, 2)}/${zeroPad(date.getDate(), 2)}/${date.getFullYear()}`;
}

export const headerColor = (theme) => {
  return theme.palette.text.header;
}

export const specialColor = (theme) => {
  return theme.palette.text.special;
}

export const isListEmpty = (listObject) => {
  return !listObject || listObject.length === 0;
}

export const getDateFormatted = (theDate) => {
  const formattedDate = new Date(theDate).toLocaleDateString();
  return formattedDate;
}

export const formatValue = (data, config) => {
  const noDataValue = 'Not available';

  // If config.field is string
  if (typeof config.field === 'string') {
    // Check if date
    if (config?.isDate) {
      return (data?.[config.field]) ? getDateFormatted(data?.[config.field]) : noDataValue
    } else {
      // Assume its a 'regular' field
      return data[config.field] || noDataValue;
    }
  }

  // If config.field is an array - cycle through and set value to first non-empty value
  if (Array.isArray(config.field)) {
    let value = noDataValue;
    config.field.forEach((field) => {
      if (field) {
        value = data[field] || value;
      }
    });
    return value;
  }
}

/* This function replaces any occurrences of - or _ followed by a lowercase letter with the letter in uppercase.
  The result is a string in camel case. */
export const snakeToCamelCase = (str) => {
  return str.replace(/([-_][a-z])/g, (group) => {
    return group.toUpperCase().replace('-', '').replace('_', '');
  });
}

/* This function gets a string in snake case and transforms it to Pascal Case
 * with or without spaces, depending on the optional argument's value.
 */
export const snakeToPascalCase = (str, withSpaces = false) => {
  return str
    .split('_')
    .map((word, index) => {
      return index === 0
        ? word[0].toUpperCase() + word.slice(1).toLowerCase()
        : word[0].toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(withSpaces ? ' ' : '');
};

/* This method parses the intersectionString to check whether it has any floating numbers on it or not.
   If it does not, or if the string is falsy, it returns the string as is.
   If it does, it returns a modified string which will have the floating numbers with only up to two decimal digits.
   The result is returned as a string. */
export const parseIntersection = function(intersectionString) {
  if (!intersectionString) {
    return intersectionString;
  }

  intersectionString = intersectionString.toString();

  // Regular expression to match a floating point number with up to two decimal digits
  const floatRegex = /-?\d+(?:\.\d+)?/g;

  // Search for floating point numbers in the string using the regex
  let matches = intersectionString.match(floatRegex);

  // If there are no matches, return the original string
  if (!matches || matches.length === 0) {
    return intersectionString;
  }

  // Otherwise, format each match to have up to two decimal digits
  for (let match of matches) {
    let floatVal = parseFloat(match);
    if (!isNaN(floatVal)) {
      let decimalCount = (match.split('.')[1] || []).length;
      if (decimalCount > 2) {
        let formattedVal = floatVal.toFixed(2);
        intersectionString = intersectionString.replace(match, formattedVal);
      }
    }
  }

  // Return the modified string
  return intersectionString;
};
